'use client';
import { LinkIcon } from '@/assets/icons/LinkIcon';
import styles from './popovermenu.module.scss';
import { Link } from '@/ui-components/atoms/Link/Link';
import { LinkSet, LinkSetProps } from '@/ui-components/molecules/LinkSet/LinkSet';
import { Text } from '@/ui-components/atoms/Text/Text';
import cn from 'classnames';
import { ReactNode, useRef } from 'react';
import useClickOutside from '@/hooks/useClickOutside';

export interface PopoverMenuProps {
    linkSet?: LinkSetProps;
    mainHeading?: string;
    mainHeadingLink?: string;
    open?: boolean;
    fullWidthContent?: ReactNode;
    onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    className?: string;
    onClose?: () => void;
    buttonRef?: React.RefObject<HTMLButtonElement>;
}

export function PopoverMenu({
    linkSet,
    mainHeading,
    mainHeadingLink,
    open,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    fullWidthContent,
    className,
    onClose,
    buttonRef,
}: PopoverMenuProps): React.JSX.Element {
    const popoverBasicStyles = fullWidthContent ? styles.fullSize : styles.card;

    const wrapperClassNames = cn(className, popoverBasicStyles, {
        [styles.show]: open,
    });

    const wrapperRef = useRef<HTMLDivElement>(null);

    useClickOutside(
        wrapperRef,
        {},
        () => {
            if (onClose) {
                onClose();
            }
        },
        buttonRef,
    );

    const content = fullWidthContent ? (
        <>{fullWidthContent}</>
    ) : (
        <>
            {mainHeading && mainHeadingLink && (
                <Link href={mainHeadingLink} className={styles.mainHeading}>
                    <Text tag="span">{mainHeading}</Text>
                    <LinkIcon />
                </Link>
            )}
            {linkSet?.links?.length && (
                <div className={styles.linkSetWrapper}>
                    <LinkSet {...linkSet} />
                </div>
            )}
        </>
    );

    return (
        <div
            className={wrapperClassNames}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onKeyDown={onKeyDown}
            role="menu"
            tabIndex={-1}
            ref={wrapperRef}
        >
            {content}
        </div>
    );
}
